// Anna Chat Service — Client-side SSE stream consumer
//
// Pure async generator that calls the Vercel API route and yields
// parsed stream events. No React dependency — usable from any hook.

import { getAuth } from 'firebase/auth';

/** Get the current user's Firebase ID token for API authentication. */
async function getAuthToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export interface StreamEvent {
  type: 'text_delta' | 'tool_use' | 'message_stop' | 'error';
  text?: string;
  toolId?: string;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  error?: string;
}

export interface AnnaChatOptions {
  system: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  tools?: unknown[];
  model?: string;
}

/**
 * Stream a chat response from Anna (Claude Haiku) via the Vercel API route.
 * Yields StreamEvent objects as they arrive over SSE.
 */
export async function* streamAnnaChat(
  options: AnnaChatOptions,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
  let response: Response;

  try {
    const token = await getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    response = await fetch('/api/chat', {
      method: 'POST',
      headers,
      body: JSON.stringify(options),
      signal,
    });
  } catch (err) {
    yield {
      type: 'error',
      error: err instanceof Error ? err.message : 'Network error',
    };
    return;
  }

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      errorMsg = body.error || errorMsg;
    } catch {
      // Use status code message
    }
    yield { type: 'error', error: errorMsg };
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    yield { type: 'error', error: 'No response body' };
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE format: "event: <type>\ndata: <json>\n\n"
      const events = buffer.split('\n\n');
      // Keep the last incomplete chunk in the buffer
      buffer = events.pop() || '';

      for (const raw of events) {
        if (!raw.trim()) continue;

        let eventType = '';
        let eventData = '';

        for (const line of raw.split('\n')) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7);
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }

        if (!eventType || !eventData) continue;

        try {
          const parsed = JSON.parse(eventData);

          switch (eventType) {
            case 'text_delta':
              yield { type: 'text_delta', text: parsed.text };
              break;
            case 'tool_use':
              yield {
                type: 'tool_use',
                toolId: parsed.id,
                toolName: parsed.name,
                toolInput: parsed.input,
              };
              break;
            case 'message_stop':
              yield { type: 'message_stop' };
              break;
            case 'error':
              yield { type: 'error', error: parsed.error };
              break;
          }
        } catch {
          // Skip malformed SSE events
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Extract completed tool calls from a list of stream events.
 */
export function collectToolCalls(
  events: StreamEvent[],
): Array<{ id: string; name: string; input: Record<string, unknown> }> {
  return events
    .filter((e): e is StreamEvent & { toolId: string; toolName: string } =>
      e.type === 'tool_use' && !!e.toolId && !!e.toolName,
    )
    .map((e) => ({
      id: e.toolId,
      name: e.toolName,
      input: e.toolInput || {},
    }));
}
